import React from 'react';
import { motion } from 'framer-motion';

const squares = [
  { size: 90, x: '5%', y: '10%', delay: 0, dur: 8, rot: 45, color: 'cyan', opacity: 0.12, filled: false },
  { size: 55, x: '18%', y: '65%', delay: 0.5, dur: 7, rot: -30, color: 'blue', opacity: 0.1, filled: true },
  { size: 40, x: '82%', y: '15%', delay: 1, dur: 9, rot: 60, color: 'cyan', opacity: 0.15, filled: false },
  { size: 70, x: '88%', y: '55%', delay: 1.5, dur: 6.5, rot: -45, color: 'blue', opacity: 0.08, filled: false },
  { size: 30, x: '45%', y: '8%', delay: 0.3, dur: 7.5, rot: 20, color: 'cyan', opacity: 0.14, filled: true },
  { size: 65, x: '92%', y: '35%', delay: 2, dur: 8.5, rot: -60, color: 'cyan', opacity: 0.1, filled: false },
  { size: 35, x: '3%', y: '40%', delay: 0.8, dur: 6, rot: 30, color: 'blue', opacity: 0.12, filled: true },
  { size: 50, x: '60%', y: '78%', delay: 1.2, dur: 7.8, rot: -20, color: 'cyan', opacity: 0.09, filled: false },
  { size: 45, x: '35%', y: '20%', delay: 0.2, dur: 9.5, rot: 70, color: 'blue', opacity: 0.11, filled: true },
  { size: 80, x: '70%', y: '25%', delay: 1.8, dur: 8.2, rot: -55, color: 'cyan', opacity: 0.07, filled: false },
  { size: 25, x: '25%', y: '80%', delay: 2.5, dur: 6.8, rot: 40, color: 'cyan', opacity: 0.13, filled: true },
  { size: 60, x: '50%', y: '50%', delay: 0.7, dur: 10, rot: -35, color: 'blue', opacity: 0.06, filled: false },
  { size: 20, x: '78%', y: '70%', delay: 1.3, dur: 5.5, rot: 15, color: 'cyan', opacity: 0.15, filled: true },
  { size: 100, x: '12%', y: '25%', delay: 0.1, dur: 11, rot: -10, color: 'blue', opacity: 0.05, filled: false },
  { size: 28, x: '55%', y: '35%', delay: 2.2, dur: 7.2, rot: 55, color: 'cyan', opacity: 0.12, filled: true },
  { size: 75, x: '40%', y: '68%', delay: 1.6, dur: 8.8, rot: -70, color: 'blue', opacity: 0.07, filled: false },
  { size: 22, x: '65%', y: '12%', delay: 0.9, dur: 6.2, rot: 35, color: 'cyan', opacity: 0.14, filled: true },
  { size: 48, x: '8%', y: '85%', delay: 2.8, dur: 7.6, rot: -25, color: 'blue', opacity: 0.1, filled: false },
  { size: 38, x: '95%', y: '75%', delay: 0.4, dur: 8.3, rot: 50, color: 'cyan', opacity: 0.11, filled: true },
  { size: 55, x: '30%', y: '45%', delay: 1.1, dur: 9.2, rot: -40, color: 'cyan', opacity: 0.06, filled: false },
  { size: 18, x: '48%', y: '88%', delay: 2.0, dur: 5.8, rot: 25, color: 'blue', opacity: 0.16, filled: true },
  { size: 42, x: '72%', y: '48%', delay: 0.6, dur: 7.4, rot: -15, color: 'cyan', opacity: 0.08, filled: false },
  { size: 32, x: '15%', y: '55%', delay: 1.9, dur: 6.4, rot: 65, color: 'blue', opacity: 0.13, filled: true },
  { size: 85, x: '58%', y: '5%', delay: 2.3, dur: 10.5, rot: -50, color: 'cyan', opacity: 0.05, filled: false },
  { size: 15, x: '42%', y: '40%', delay: 0.0, dur: 5.0, rot: 80, color: 'blue', opacity: 0.18, filled: true },
];

const diamonds = [
  { x: '22%', y: '18%', delay: 0.3, dur: 8, size: 18 },
  { x: '75%', y: '30%', delay: 1.8, dur: 7, size: 14 },
  { x: '38%', y: '58%', delay: 2.2, dur: 9, size: 20 },
  { x: '85%', y: '68%', delay: 0.9, dur: 6.5, size: 12 },
  { x: '10%', y: '72%', delay: 1.4, dur: 7.5, size: 16 },
  { x: '55%', y: '22%', delay: 2.5, dur: 8.5, size: 10 },
  { x: '68%', y: '82%', delay: 0.6, dur: 6, size: 15 },
  { x: '30%', y: '12%', delay: 1.7, dur: 9.5, size: 13 },
];

const HeroBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {/* Large gradient orbs */}
      <motion.div
        animate={{ x: [0, 40, -30, 0], y: [0, -50, 30, 0], scale: [1, 1.15, 0.9, 1] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-10 left-[5%] w-[600px] h-[600px] rounded-full bg-cyan-500/[0.06] blur-[120px]"
      />
      <motion.div
        animate={{ x: [0, -50, 40, 0], y: [0, 40, -40, 0], scale: [1, 0.85, 1.2, 1] }}
        transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-32 right-[0%] w-[500px] h-[500px] rounded-full bg-blue-500/[0.06] blur-[120px]"
      />
      <motion.div
        animate={{ x: [0, 30, -40, 0], y: [0, -30, 50, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute bottom-10 left-[25%] w-[400px] h-[400px] rounded-full bg-cyan-400/[0.04] blur-[100px]"
      />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `linear-gradient(rgba(6,182,212,0.4) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(6,182,212,0.4) 1px, transparent 1px)`,
          backgroundSize: '50px 50px',
        }}
      />

      {/* Floating squares - large batch */}
      {squares.map((sq, i) => (
        <motion.div
          key={`sq-${i}`}
          initial={{ opacity: 0, scale: 0.3 }}
          animate={{
            opacity: [0, sq.opacity, sq.opacity, 0],
            scale: [0.5, 1, 1, 0.5],
            y: [0, -20 - (i % 5) * 8, 10 + (i % 3) * 5, 0],
            rotate: [sq.rot, sq.rot + 90, sq.rot + 180, sq.rot + 270],
          }}
          transition={{ duration: sq.dur, delay: sq.delay, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute"
          style={{ left: sq.x, top: sq.y }}
        >
          {sq.filled ? (
            <div
              className={`rounded-md ${sq.color === 'cyan' ? 'bg-cyan-400/[0.08]' : 'bg-blue-400/[0.08]'}`}
              style={{ width: sq.size, height: sq.size }}
            />
          ) : (
            <div
              className={`rounded-lg border ${sq.color === 'cyan' ? 'border-cyan-400/[0.15]' : 'border-blue-400/[0.15]'}`}
              style={{ width: sq.size, height: sq.size }}
            />
          )}
        </motion.div>
      ))}

      {/* Diamonds */}
      {diamonds.map((dm, i) => (
        <motion.div
          key={`dm-${i}`}
          animate={{
            y: [0, -20 - (i % 3) * 10, 15, 0],
            rotate: [45, 135, 225, 405],
            opacity: [0.15, 0.4, 0.15],
          }}
          transition={{ duration: dm.dur, delay: dm.delay, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute"
          style={{ left: dm.x, top: dm.y }}
        >
          <div
            className="border border-cyan-400/25 rotate-45"
            style={{ width: dm.size, height: dm.size }}
          />
        </motion.div>
      ))}

      {/* Dotted rings */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
        className="absolute top-[8%] right-[12%] w-72 h-72 rounded-full border border-dashed border-cyan-400/[0.08]"
      />
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 55, repeat: Infinity, ease: 'linear' }}
        className="absolute bottom-[10%] left-[8%] w-56 h-56 rounded-full border border-dashed border-blue-400/[0.08]"
      />
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
        className="absolute top-[40%] left-[45%] w-40 h-40 rounded-full border border-dotted border-cyan-400/[0.05]"
      />

      {/* Scan lines */}
      <motion.div
        animate={{ x: ['-100%', '250%'] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'linear', repeatDelay: 5 }}
        className="absolute top-[40%] w-[25%] h-px bg-gradient-to-r from-transparent via-cyan-400/25 to-transparent"
      />
      <motion.div
        animate={{ x: ['-100%', '250%'] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'linear', repeatDelay: 3 }}
        className="absolute top-[70%] w-[20%] h-px bg-gradient-to-r from-transparent via-blue-400/20 to-transparent"
      />
      <motion.div
        animate={{ y: ['-100%', '250%'] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'linear', repeatDelay: 4 }}
        className="absolute left-[55%] h-[18%] w-px bg-gradient-to-b from-transparent via-cyan-400/15 to-transparent"
      />
    </div>
  );
};

export default HeroBackground;
